import { uniq } from 'lodash';
import { apiGetMaterials, apiGetMaterialTextures } from '../../../../helpers/api';
import { fetchAllPagesByIdList } from '../../../../helpers/apiHelper';
import { updateDbMaterial, updateDbMaterialTextures } from '../../../../helpers/idb';
import { parseArchiveProjectTextures } from './parseArchiveProjectTextures';
import * as utils from './utils'

export const loadZipV1MaterialDetails = async ({
  materialDetails,
  shouldUploadTextures,
  entries,
  projectJson,
}) => {
  const isNonMaterialProject = utils.isNonMaterialProject(projectJson);
  const getFileExtension = (str) => {
    if (!str) return null;
    const result = str.match(/\.[0-9a-z]+$/i)
    return result?.[0] || null;
  };

  let imagesMap;

  if (shouldUploadTextures) {
    imagesMap = await parseArchiveProjectTextures({
      entries,
      projectJson,
      projectJsonVersion: 1,
      isNonMaterialProject,
    });
  }

  // load old materials
  const materialsCache = {};
  let oldMaterialIds = [];
  materialDetails.forEach((m) => {
    if (m.isMaterialChanged) {
      const id = isNonMaterialProject
        ? m.loadedMatName
        : m.loadedMaterialId
      oldMaterialIds.push(id);
    }
  });
  oldMaterialIds = uniq(oldMaterialIds);

  const fetchedMaterials = await fetchAllPagesByIdList({
    idList: oldMaterialIds,
    api: (idList) => {
      const query = isNonMaterialProject
        ? idList.map((id) => `main_asset_name=${id}`).join('&')
        : idList.map((id) => `old_id=${id}`).join('&');
      return apiGetMaterials(query);
    },
  });

  fetchedMaterials.forEach((m) => {
    const key = isNonMaterialProject ? m.main_asset_name?.toLowerCase() : m.old_material_id
    materialsCache[key] = m;
  });

  // parse objects data

  // ------------START------------------ //
  // parseMaterialV1
  const parseMaterialV1 = async (objectData) => {
    if (objectData.textureChanged && !objectData.isTexturefromMaterialPreset) {
      if (shouldUploadTextures) {
        const texture = imagesMap[objectData.diffusePath];
        objectData.diffusePath = texture?.file || 'MISSING';
        objectData.diffuseId = texture?.id || 'MISSING';
        objectData.diffuseName = texture?.name || null;
        objectData.diffuseVersion = texture?.version || null;
      } else {
        objectData.diffusePath = '';
      }
    }

    if (objectData.AOMapChanged) {
      if (shouldUploadTextures) {
        const texture = imagesMap[objectData.AOPath];
        objectData.AOImageFormat = getFileExtension(objectData.AOPath);
        objectData.AOPath = texture?.file || 'MISSING';
        objectData.AOId = texture?.id || 'MISSING';
      } else {
        objectData.AOPath = '';
      }
    }

    if (objectData.roughnessMapChanged) {
      if (shouldUploadTextures) {
        const texture = imagesMap[objectData.roughnessPath];
        objectData.roughnessImageFormat = getFileExtension(objectData.roughnessPath);
        objectData.roughnessPath = texture?.file || 'MISSING';
        objectData.roughnessId = texture?.id || 'MISSING';
      } else {
        objectData.roughnessPath = '';
      }
    }

    if (objectData.normalChanged) {
      if (shouldUploadTextures) {
        const texture = imagesMap[objectData.normalPath];
        objectData.normalImageFormat = getFileExtension(objectData.normalPath);
        objectData.normalPath = texture?.file;
        objectData.normalId = texture?.id || 'MISSING';
        objectData.normalMapId = texture?.id || 'MISSING';
      } else {
        objectData.normalPath = '';
      }
    }

    if (objectData.isTransparencyMapChanged) {
      if (shouldUploadTextures) {
        const texture = imagesMap[objectData.transparencyPath];
        objectData.transparencyImageFormat = getFileExtension(objectData.transparencyPath);
        objectData.transparencyPath = texture?.file || 'MISSING';
        objectData.transparencyId = texture?.id || 'MISSING';
      } else {
        objectData.transparencyPath = '';
      }
    }

    if (objectData.isMaterialChanged) {
      // const materialsResponse = await apiGetMaterials(`old_id=${objectData.loadedMaterialId}`);
      // const material = materialsResponse.results[0];
      const material = materialsCache[objectData.loadedMaterialId];

      // if (!material) {
      //   errorToast('Unable to load material data. LF_68688', { toastId: 'LF_68688'});
      //   throw new Error('Unable to load material data. LF_68688');
      // }

      let dbMaterial;
      const oldId = objectData.loadedMaterialId;
      try {
        dbMaterial = await updateDbMaterial(material);
      } catch (e) {
        // console.error(e);
        console.error({ id: `old_id=${oldId}`, erorCode: 'LF_32472342342', e });
        // TODO: handle errors if needed
      }

      objectData.oldLoadedMaterialId = oldId;
      objectData.loadedMaterialId = material?.id || 'MISSING';
      objectData.materialId = material?.id || 'MISSING';
      objectData.loadedMatName = material?.name || objectData.loadedMatName;
      objectData.loadedMaterialPath = dbMaterial?.file || 'MISSING';
    } else {
      objectData.loadedMaterialId = '';
      objectData.materialId = '';
      objectData.loadedMatName = '';
      objectData.loadedMaterialPath = '';
    }

    if (objectData.materialTextureId) {
      const materialsResponse = await apiGetMaterialTextures(
        `old_texture_id=${objectData.materialTextureId}`
      );
      const material = materialsResponse.results[0];

      let dbMaterial;
      try {
        dbMaterial = await updateDbMaterialTextures(material);
      } catch (e) {
        console.error(e);
        // TODO: handle errors if needed
      }

      objectData.materialTextureId = material?.id || 'MISSING';
      objectData.materialTexturePath = dbMaterial?.file || 'MISSING';
    }

    return formatV1MaterialData(objectData);
  };
  // ----------END-------------------- //

  // ----------START------------------- //
  // parseNonMaterialV1
  const parseNonMaterialV1 = async (objectData) => {
    // do not use spread operator here as we're skipping some values
    const data =  {
      ObjectName: objectData.ObjectName,
      offsetX: objectData.offsetX,
      offsetY: objectData.offsetY,
      scaleX: objectData.scaleX,
      scaleY: objectData.scaleY,
      color: objectData.color,
      color_blender: objectData.color_blender,
      textureChanged: objectData.textureChanged,
      normalChanged: objectData.normalChanged,
      OtherObjectName: objectData.OtherObjectName,
      loadedMatName: '',
      isNormal: objectData.isNormal,
      isMaterialChanged: objectData.isMaterialChanged,
      isTexture: objectData.isTexture,
      isColor: objectData.isColor,
      normalImageFormat: objectData.normalFormat,
      textureFormat: objectData.textureFormat,
      objectUniqueIdentifier: objectData.objectUniqueIdentifier,

      AOPath: '',
      normalPath: '',
      roughnessPath: '',
      transparencyPath: '',
      metallicPath: '',
    };

    if (objectData.textureChanged) {
      if (shouldUploadTextures) {
        const key = objectData.ObjectName + '_texture.jpg';
        const texture = imagesMap[key];
        data.diffusePath = texture?.file || 'MISSING';
        data.diffuseId = texture?.id || 'MISSING';
      } else {
        data.diffuseId = objectData.diffuseId
        data.diffusePath = '';
      }
    }

    if (data.isMaterialChanged) {
      const material = materialsCache[objectData.loadedMatName];

      let dbMaterial;
      try {
        dbMaterial = await updateDbMaterial(material);
      } catch (e) {
        // console.error(e);
        console.error({ id: `loadedMatName=${objectData.loadedMatName}`, erorCode: 'LF_32472342jjs342', e });
        // TODO: handle errors if needed
      }

      data.oldLoadedMaterialId = material?.old_material_id || null;
      data.loadedMaterialId = material?.id || 'MISSING';
      data.materialId = material?.id || 'MISSING';
      data.loadedMatName = material?.name || data.loadedMatName;
      data.loadedMaterialPath = dbMaterial?.file || 'MISSING';
    } else {
      data.loadedMaterialId = '';
      data.materialId = '';
      data.loadedMatName = '';
      data.loadedMaterialPath = '';
    }

    return formatV1MaterialData(data);
  };
  // -----------END------------------- //

  const parsedMaterialDetails = await Promise.all(
    materialDetails.map(isNonMaterialProject ? parseNonMaterialV1 : parseMaterialV1)
  );

  return parsedMaterialDetails;
};

const formatV1MaterialData = (d) => {
  return {
    diffusePath: d.diffusePath,
    loadedMaterialPath: d.loadedMaterialPath,
    oldLoadedMaterialId: d.oldLoadedMaterialId,
    materialTexturePath: d.materialTexturePath || null,
    AOPath: d.AOPath,
    normalPath: d.normalPath,
    roughnessPath: d.roughnessPath,
    transparencyPath: d.transparencyPath,
    normalId: d.normalId,
    AOId: d.AOId,
    metallicId: '',
    metallicPath: '',
    materialId: d.materialId,
    version: null,
    isMaterialChanged: d.isMaterialChanged,
    objectName: d.ObjectName,
    diffuseId: d.diffuseId,
    emissionIntensity: 0,
    effectLightColorBounce: false,
    isMetallicMap: false,
    isMetallicMapChanged: false,
    isCustomMetallicMap: false,
    MetallicMapChangeable: false,
    aoIntensity: typeof d.aoIntensity === 'number' ? d.aoIntensity : 1,
    copiedFromMaterialName: '',
    copiedFromObjectName: '',
    loadedMaterialId: d.loadedMaterialId,
    loadedMatName: d.loadedMatName,
    materialTextureName: '',
    materialMainAssetName: '',
    materialTextureId: d.materialTextureId,
    isTexture: d.isTexture,
    isTextureChanged: d.textureChanged,
    textureFormat: d.textureFormat,
    diffuseName: d.diffuseName,
    isAOMap: d.isAOMap,
    AOMapChanged: d.AOMapChanged,
    isCustomAOMap: d.isCustomAOMap || (d.isAOMap && d.AOMapChanged),
    AOImageFormat: d.AOImageFormat,
    isNormal: d.isNormal,
    normalChanged: d.normalChanged,
    normalIntensity: d.normalIntensity,
    isNormalInvert: d.isNormalInvert,
    isCustomNormalMap: d.isCustomNormalMap || (d.isNormal && d.normalChanged),
    normalMapId: d.normalMapId,
    normalImageFormat: d.normalImageFormat,
    isRoughnessMap: d.isRoughnessMap,
    roughnessMapChanged: d.roughnessMapChanged,
    roughnessIntensity: d.roughnessIntensity,
    isRoughnessInvert: d.isRoughnessInvert,
    isCustomRoughnessMap: d.isCustomRoughnessMap || (d.isRoughnessMap && d.roughnessMapChanged),
    roughnessId: d.roughnessId,
    roughnessImageFormat: d.roughnessImageFormat,
    isTransparencyMapChanged: d.isTransparencyMapChanged,
    isTransparency: d.isTransparency,
    isTransparencyInvert: d.isTransparencyInvert,
    transparencyIntensity: d.transparencyIntensity,
    isCustomTransparencyMap: d.isCustomTransparencyMap,
    isTransparencyFromAlbedo: d.isTransparencyFromAlbedo,
    transparencyId: d.transparencyId,
    transparencyImageFormat: d.transparencyImageFormat,
    metalIntensity: d.metalIntensity || 0,
    initialMetalIntensity: d.initialMetalIntensity || 0,
    matName: d.matName,
    UV0: d.UV0,
    isAdvanced: d.isAdvanced,
    isCurrentMapsGenerated: d.isCurrentMapsGenerated,
    isTexturefromMaterialPreset: d.isTexturefromMaterialPreset,
    isColor: d.isColor,
    color: d.color,
    colorBlender: d.color_blender,
    OtherObjectName: d.OtherObjectName,
    tilingOffsetValues: d.tilingOffsetValues,
    tilingOptionsData: d.tilingOptionsData,
    togetherTilingKeys: d.togetherTilingKeys,
    objectUniqueIdentifier: d.objectUniqueIdentifier,

    ...(d.offsetX ? { offsetX: d.offsetX } : {}),
    ...(d.offsetY ? { offsetY: d.offsetY } : {}),
    ...(d.scaleX ? { scaleX: d.scaleX } : {}),
    ...(d.scaleY ? { scaleY: d.scaleY } : {}),

    ...(d.aoVersion ? { aoVersion: d.aoVersion } : {}),
    ...(d.diffuseVersion ? { diffuseVersion: d.diffuseVersion } : {}),
    ...(d.normalVersion ? { normalVersion: d.normalVersion } : {}),
    ...(d.roughnessVersion ? { roughnessVersion: d.roughnessVersion } : {}),
    ...(d.transparencyVersion ? { transparencyVersion: d.transparencyVersion } : {}),

    ...(d.aoMapName ? { aoMapName: d.aoMapName } : {}),
    ...(d.diffuseName ? { diffuseName: d.diffuseName } : {}),
    ...(d.normalMapName ? { normalMapName: d.normalMapName } : {}),
    ...(d.roughnessMapName ? { roughnessMapName: d.roughnessMapName } : {}),
    ...(d.transparencyMapName ? { transparencyMapName: d.transparencyMapName } : {}),
  };
};
